import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
	Tabs,
	Tab,
	Tooltip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import {
	API_TIME_FORMAT,
	DISPLAY_DATE_FORMAT,
	DISPLAY_DATE_MINUTE_FORMAT,
} from '@/utils/dateHelper';
import dayjs from 'dayjs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MeditationListSummaryFilterSection from './components/CentreInterventionSummaryFilterSection';
import { CaseFilter, CasePageDTO } from '@/api/DTO/case-management/caseDto';
import { useGetCentreInterventionListQuery } from '@/api/case-management/caseApi';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { PermissionEnum } from '@/api/enum/common.enum';
import { useGetUser } from '@/hooks/useGetUser';

export default function CentreInterventionSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();
	const { userInfo } = useGetUser();

	const defaultPropertyFilter: CaseFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		castNoteStatusList: [],
		additionalList: [],
		natureList: [],
		interventionWorkerList: [],
		submittedByList: [],
	};
	const defaultValues: DefaultValues<CaseFilter> = defaultPropertyFilter;
	const methods = useForm<CaseFilter>({
		defaultValues,
	});
	const transformedSearchParams: CaseFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'castNoteStatusList',
			'additionalList',
			'natureList',
			'interventionWorkerList',
			'submittedByList',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetCentreInterventionListQuery(
			{
				...transformedSearchParams,
				type: 1,
				dateRangeStart: methods.getValues('dateRangeStart'),
				dateRangeEnd: methods.getValues('dateRangeEnd'),
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback((response?: any[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				interventionDate: !item.interventionDate
					? '-'
					: dayjs(item.interventionDate)
							.locale('en')
							.format(DISPLAY_DATE_FORMAT),
				interventionStartTime: !item.interventionStartTime
					? '-'
					: dayjs(item.interventionStartTime, API_TIME_FORMAT)
							.locale('en')
							.format(DISPLAY_DATE_MINUTE_FORMAT),
				interventionEndTime: !item.interventionEndTime
					? '-'
					: dayjs(item.interventionEndTime, API_TIME_FORMAT)
							.locale('en')
							.format(DISPLAY_DATE_MINUTE_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as CaseFilter;

			const cleanedFilter = Object.fromEntries(
				Object.entries(propertyFilterFormResult).filter(([key, val]) => {
					return val !== '' && val != null;
				})
			);

			// append to url to reload page
			setSearchParams(qs.stringify(cleanedFilter));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const columns: GridColDef[] = [
		{
			field: 'fileNo',
			headerName: t('member.fileNo'),
			flex: 15,
		},
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 15,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>{params.row.nameEn}</Typography>
					<Typography variant='body2'>{params.row.nameZh}</Typography>
				</Box>
			),
		},
		{
			field: 'interventionDate',
			headerName: t('case.date'),
			flex: 15,
		},
		{
			field: 'interventionStartTime',
			headerName: t('case.startTime'),
			flex: 15,
		},
		{
			field: 'interventionEndTime',
			headerName: t('case.endTime'),
			flex: 15,
		},
		{
			field: 'natureDesc',
			headerName: t('case.nature'),
			flex: 15,
		},

		{
			field: 'additionalDesc',
			headerName: t('case.additional'),
			flex: 15,
		},
		{
			field: 'riskLevelDesc',
			headerName: t('case.riskLevel'),
			flex: 15,
		},

		{
			field: 'familyInvolvedAlliedHealthServicesEscortDesc',
			headerName: t('case.familyInvolvedAlliedHealthServicesEscort'),
			flex: 15,
		},
		{
			field: 'supportingStaffDesc',
			headerName: t('case.supportingStaff'),
			flex: 15,
		},
		{
			field: 'interventionWorkerName',
			headerName: t('case.inWorker'),
			flex: 15,
		},
		{
			field: 'submittedByName',
			headerName: t('case.submittedBy'),
			flex: 15,
		},
		{
			field: 'caseNoteStatusDesc',
			headerName: t('case.caseNoteStatus'),
			flex: 15,
		},
		{
			field: 'edit',
			headerName: '',

			width: 68,
			renderCell: (params) => (
				<Link to={`/case-management/centre-intervention/detail/${params.id}`}>
					<IconButton color='secondary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.centreInterventionSummary')}
				</Typography>
				{userInfo?.permissionList?.includes(
					PermissionEnum.CaseManagementCreateIntervention
				) && (
					<Button
						endIcon={<AddCircleOutlineOutlinedIcon />}
						onClick={() => {
							navigate(
								`/case-management/centre-intervention/create/centre-intervention`
							);
						}}
						variant='contained'
						sx={{ marginRight: '20px' }}
					>
						{t('button.newIntervention')}
					</Button>
				)}
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					{' '}
					<FormProvider {...methods}>
						<MeditationListSummaryFilterSection></MeditationListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
